// The kind catalog of resources.md §2.1 (46 public kinds), in the shape API discovery returns. It is the console's
// interim registry until tools/linkgen (F3) generates the TypeScript registry from discovery; the generated one must
// agree with this table (the linkTo totality test runs over both).

export type KindScope = "project" | "org" | "user";

export interface KindInfo {
  kind: string;
  version: "v1" | "v1beta1";
  scope: KindScope;
  /** The REST plural, also the console URL segment (ADR-107). */
  resource: string;
  shortNames: string[];
  categories: string[];
  /** TypeID prefix of `metadata.uid`. */
  idPrefix: string;
  /** g l w c u p d. */
  verbs: string;
  subresources: string[];
  /** Subresources that start stopped, billable compute (ADR-097, ui-spec §4.16): never preloaded. */
  wakes?: string[];
}

const k = (
  kind: string,
  version: KindInfo["version"],
  scope: KindScope,
  resource: string,
  shortNames: string[],
  categories: string[],
  idPrefix: string,
  verbs: string,
  subresources: string[] = [],
  wakes?: string[],
): KindInfo => ({ kind, version, scope, resource, shortNames, categories, idPrefix, verbs, subresources, ...(wakes ? { wakes } : {}) });

export const KINDS: readonly KindInfo[] = [
  k("MarketplaceEntry", "v1", "org", "marketplaceentries", [], ["marketplace"], "mke", "glwcupd", ["status"]),
  k("MarketplaceContract", "v1", "org", "marketplacecontracts", [], ["marketplace"], "mkc", "glw"),
  k("Job", "v1", "project", "jobs", ["job"], ["all", "compute", "runs"], "job", "glwcupd", ["status", "log", "processes", "portforward", "files", "outputs", "checkpoints", "taskevents"]),
  k("Pipeline", "v1", "project", "pipelines", ["pl"], ["all", "compute", "runs"], "pl", "glwcupd", ["status"]),
  k("Sweep", "v1", "project", "sweeps", ["swp"], ["all", "compute", "runs"], "swp", "glwcupd", ["status"]),
  k("Attempt", "v1", "project", "attempts", ["att"], ["compute"], "att", "glw", ["log"]),
  k("Sandbox", "v1", "project", "sandboxes", ["sb"], ["all", "compute"], "sbx", "glwcupd", ["status", "log", "processes", "files", "portforward", "previews"], ["files", "previews", "processes", "portforward"]),
  k("Process", "v1", "project", "processes", ["proc"], ["compute"], "prc", "glwpd", ["attach", "output", "stdin", "signals"]),
  k("Workspace", "v1", "project", "workspaces", ["ws"], ["all", "compute"], "wsp", "glwcupd", ["status", "log", "processes", "files", "portforward", "previews", "ssh", "sharing", "release-capacity"], ["previews", "ssh", "processes"]),
  k("App", "v1", "project", "apps", ["app"], ["all", "compute"], "app", "glwcupd", ["status"]),
  k("Function", "v1", "project", "functions", ["fn"], ["all", "compute"], "fn", "glwcupd", ["status", "log", "claims"]),
  k("FunctionCall", "v1", "project", "functioncalls", ["fnc"], ["compute"], "fnc", "glwcpd", ["result"]),
  k("Agent", "v1", "project", "agents", ["agent"], ["all", "ai"], "agt", "glwcupd", ["status", "log", "revisions", "sessions"]),
  k("AgentRun", "v1", "project", "agentruns", ["ar"], ["all", "ai"], "arun", "glwcpd", ["status", "log", "messages", "resolutions", "retries", "steps", "answer", "team", "team-controls", "outputs"]),
  k("AgentGroup", "v1", "project", "agentgroups", ["ag"], ["all", "ai"], "agrp", "glwcupd", ["status", "results"]),
  k("Model", "v1", "project", "models", ["mdl"], ["ai"], "mdl", "glw"),
  k("InferenceEndpoint", "v1", "project", "inferenceendpoints", ["iep", "ep"], ["all", "ai"], "iep", "glwcupd", ["status"]),
  k("Environment", "v1", "project", "environments", ["env"], ["ai"], "env", "glwcupd", ["status"]),
  k("TrainingJob", "v1beta1", "project", "trainingjobs", ["tj"], ["all", "ai", "runs"], "tj", "glwcupd", ["status", "gradings", "taskevents", "outputs"]),
  k("TrainingRuntime", "v1beta1", "project", "trainingruntimes", ["trt", "tr"], ["ai"], "trt", "glwcupd", ["status", "datasetpreviews"]),
  k("Volume", "v1", "project", "volumes", ["vol"], ["storage"], "vol", "glwcupd", ["status", "uploads", "revisions"]),
  k("Secret", "v1", "project", "secrets", ["sec"], ["storage"], "sec", "glwcupd"),
  k("Image", "v1", "project", "images", ["img"], ["storage"], "img", "glwcupd", ["status", "log"]),
  k("Connection", "v1", "project", "connections", ["conn"], ["storage"], "con", "glwcupd", ["status", "repositories"]),
  k("Organization", "v1", "org", "organizations", ["org"], ["access"], "org", "glwcup", ["status"]),
  k("Project", "v1", "org", "projects", ["proj"], ["access"], "prj", "glwcupd"),
  k("Member", "v1", "org", "members", ["member"], ["access"], "mem", "glwupd"),
  k("Invite", "v1", "org", "invites", ["invite"], ["access"], "inv", "glwcpd"),
  k("APIKey", "v1", "org", "apikeys", ["key"], ["access"], "key", "glwcpd"),
  k("ServiceAccount", "v1", "project", "serviceaccounts", ["sa"], ["access"], "sa", "glwcupd", ["tokens"]),
  k("OAuthGrant", "v1", "org", "oauthgrants", ["oauth"], ["access"], "oag", "glwcd"),
  k("SSHKey", "v1", "user", "sshkeys", ["sshkey"], ["access"], "ssk", "glwcpd"),
  k("Quota", "v1", "org", "quotas", ["quota"], ["access"], "quo", "glwcupd", ["status"]),
  k("WebhookEndpoint", "v1", "org", "webhookendpoints", ["wh", "webhook"], ["access"], "whk", "glwcupd", ["status", "deliveries", "rotations"]),
  k("Event", "v1", "org", "events", ["ev"], ["access"], "evt", "glw"),
  k("AuditEvent", "v1", "org", "auditevents", ["audit"], ["access"], "aud", "glw"),
  k("BillingAccount", "v1", "org", "billingaccounts", ["billing", "ba"], ["billing"], "bac", "glwup", ["status", "redemptions", "deals", "sessions"]),
  k("TopUp", "v1", "org", "topups", ["topup"], ["billing"], "top", "glwc", ["status"]),
  k("Budget", "v1", "org", "budgets", ["budget"], ["billing"], "bud", "glwcupd", ["status"]),
  k("CreditGrant", "v1", "org", "creditgrants", ["grant"], ["billing"], "grt", "glw"),
  k("Transaction", "v1", "org", "transactions", ["txn"], ["billing"], "txn", "glw"),
  k("UsageRecord", "v1", "org", "usagerecords", ["usage"], ["billing"], "use", "glw"),
  k("Offering", "v1", "org", "offerings", ["gpus", "offering"], ["billing"], "ofr", "glw"),
  k("Pool", "v1", "project", "pools", ["pool"], ["all", "compute"], "pool", "glwcupd", ["status", "utilization", "forecast", "recommendations", "sharing"]),
  k("PoolAction", "v1", "project", "poolactions", ["pact"], ["compute"], "pact", "glwp", ["status"]),
  k("Node", "v1", "org", "nodes", ["node"], ["compute"], "bnod", "glwpd", ["status"]),
  k("ReservedHost", "v1", "project", "reservedhosts", ["rh"], ["compute"], "rsvh", "gl", ["ssh", "release-capacity"]),
  k("EnrollmentToken", "v1", "project", "enrollmenttokens", ["etok"], ["compute"], "etok", "glwcd"),
  k("CloudAccount", "v1", "org", "cloudaccounts", ["cloud", "ca"], ["compute"], "cld", "glwcupd", ["status", "onboarding", "inventory", "projects", "credentials", "subscriptions", "tenants"]),
];

const byKind = new Map(KINDS.map((x) => [x.kind, x]));
const byResource = new Map(KINDS.map((x) => [x.resource, x]));
const byShortName = new Map(KINDS.flatMap((x) => [...x.shortNames, x.resource, x.kind.toLowerCase()].map((n) => [n, x] as const)));
// Longest prefix first, so `arun_` never resolves as `ar…` and `pact_` never as `pool`.
const byPrefix = [...KINDS].sort((a, b) => b.idPrefix.length - a.idPrefix.length);

export function kindInfo(kind: string): KindInfo {
  const info = byKind.get(kind);
  if (!info) throw new Error(`unknown kind ${kind}`);
  return info;
}

export function kindByResource(resource: string): KindInfo | undefined {
  return byResource.get(resource);
}

/** `job/`, `sb/`, `iep/`… the kubectl-style prefixes the palette accepts (ui-spec §2.6). */
export function kindByShortName(name: string): KindInfo | undefined {
  return byShortName.get(name.toLowerCase());
}

/** Resolves a pasted TypeID (`job_…`, `sbx_…`, `att_…`) to its kind. */
export function kindForUid(uid: string): KindInfo | undefined {
  const at = uid.lastIndexOf("_");
  if (at <= 0) return undefined;
  const prefix = uid.slice(0, at);
  return byPrefix.find((x) => x.idPrefix === prefix);
}

export function apiVersionOf(kind: string): string {
  return `nodus.dev/${kindInfo(kind).version}`;
}
